import TrailwiseEventBus from '../TrailwiseEventBus';
import { MapMode } from '../TrailwiseEvents';

/**
 * The map's interaction-mode state machine. Exactly one mode is active at a
 * time (idle / measure / editing / tracking); modules react to transitions via
 * the mapModeChanged bus event rather than sniffing each other's UI state.
 *
 * Leaving a mode can be vetoed by a registered leave guard (e.g. the editing
 * session's dirty-confirm). While editing, only a transition to idle is
 * allowed — everything else is rejected outright.
 */
export class MapModeManager {

    private _mode: MapMode = 'idle';
    private _leaveGuards = new Map<MapMode, () => Promise<boolean>>();

    get current(): MapMode {
        return this._mode;
    }

    /** Registers an async veto for leaving the given mode. One guard per mode. */
    public registerLeaveGuard = (mode: MapMode, guard: () => Promise<boolean>): void => {
        this._leaveGuards.set(mode, guard);
    }

    /**
     * Requests a transition. Returns true if the map is now in the requested
     * mode (including when it already was), false if the transition was
     * blocked or vetoed by the current mode's leave guard.
     */
    public requestMode = async (next: MapMode): Promise<boolean> => {
        if (next === this._mode) {
            return true;
        }

        if (this._mode === 'editing' && next !== 'idle') {
            // The editing toolbar is the only UI while editing; anything else
            // asking for a mode is a stray event — reject it.
            return false;
        }

        const guard = this._leaveGuards.get(this._mode);
        if (guard && !(await guard())) {
            return false;
        }

        const from = this._mode;
        this._mode = next;
        TrailwiseEventBus.mapModeChanged({ from: from, to: next });
        return true;
    }
}
